import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Custom hook that scrolls the page to the top on every navigation,
 * including back/forward (POP), push, and replace transitions, and
 * regardless of whether the URL contains a hash or query string.
 */
export const useScrollToTop = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Disable the browser's automatic scroll restoration so back/forward
  // navigation doesn't restore the previous scroll position.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if ('scrollRestoration' in window.history) {
      const previous = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => {
        window.history.scrollRestoration = previous;
      };
    }
  }, []);

  useEffect(() => {
    const scrollTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
      // Some layouts use a scrollable root container instead of window.
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollTop();
    const raf1 = requestAnimationFrame(scrollTop);
    // Second frame catches late hash-jump / restoration behavior on POP.
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(scrollTop));

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [location.pathname, location.hash, location.search, location.key, navigationType]);
};

export default useScrollToTop;
